<template>
    <b-row v-if="!IS_MOBILE || !GET_IS_MOBILE_GRAPHIC_SHOWN">
        <b-col cols="12" class="d-lg-flex flex-lg-column align-items-lg-end">
            <div class="dam-profile-card">
                <b-img
                    class="dam-profile-card-image"
                    @click="openModal"
                    v-if="cameraImage"
                    :src="cameraImage"
                    :fluid="true"
                    :alt="$t('sensors-page.camera-modal.dam-photo')"
                ></b-img>
                <div class="card-detail-text mt-2" v-if="cameraImage">
                    <span>{{ $t('dashboard.last_updated') }}</span>
                    <span>{{'&nbsp;'}}</span>
                    <span>{{shortDateCard[1]}}</span>
                    <span>{{'&nbsp;'}}</span>
                    <span>{{$t('dashboard.on')}}</span>
                    <span>{{'&nbsp;'}}</span>
                    <span>{{shortDateCard[0]}}</span>
                    <span>{{'&nbsp;'}}</span>
                    <span>{{$t('dashboard.year')}}</span>
                    <span v-if="this.$i18n.locale === 'bg'">.</span>
                </div>
                <div class="card-detail-text mt-2" v-else>
                    {{ $t('dashboard.no_images_available') }}
                </div>
            </div>
            <ProfileDamMeteorologicalStation/>
        </b-col>
        <b-col cols="12">
            <b-row class="dam-profile-card-wrapper">
                <b-col cols="6" md="12" class="d-lg-flex flex-lg-column align-items-lg-end">
                    <ProfileDamMeteorologicalForecast/>
                </b-col>
                <b-col cols="6" class="d-md-none">
                    <ProfileDamGraphicCardLink/>
                </b-col>
            </b-row>
        </b-col>
        <b-modal
            ref="camera-modal"
            size="lg"
            centered hide-footer no-close-on-esc
            header-class="border-bottom-0"
            :title="currentDamSelectedName"
        >
            <b-row no-gutters>
                {{ /* picker */ }}
                <b-col
                    cols="12" sm="4" class="modal-small-text text-center mb-2 mb-sm-0">
                    <div>{{ $t('sensors-page.camera-modal.show-images-from') }}:</div>
                    <ModalDatePicker
                        :selectedDate="selectedDate"
                        @validate="dispatchGetImg"
                    />
                </b-col>
                <b-col
                    v-if="imagesList.length > 0"
                    cols="12"
                    class="mx-auto py-3 py-sm-4"
                    :key="forceKey"
                >
                    <CarouselGalleryWrapper
                        :images="imagesList"
                    ></CarouselGalleryWrapper>
                </b-col>
            </b-row>
            <!-- General error -->
            <b-row no-gutters v-if="error">
                <b-col cols="12" class="text-center modal-text-message text-danger py-4">
                    {{ $t('sensors-page.camera-modal.error-message') }}
                </b-col>
            </b-row>
            <b-row no-gutters v-if="imagesList.length <= 0" class="py-3">
                <b-col cols="12" class="text-center modal-text-message">
                    {{ $t('sensors-page.camera-modal.no-results-message') }}
                </b-col>
                <b-col cols="12" class="text-center modal-text-message">
                    {{ $t('sensors-page.camera-modal.please-select') }}
                </b-col>
            </b-row>
        </b-modal>
    </b-row>
</template>

<script>
    // utils
    import {formatShortDate} from "../../utils/common";
    import apiCall from '../../utils/api';
    // vuex
    import {mapActions, mapGetters} from "vuex";
    import {AUTH_LOGOUT} from "../../store/actions/auth";
    import {GET_IS_MOBILE_GRAPHIC_SHOWN, IS_MOBILE} from "../../store/actions/profile";
    // components
    import ProfileDamMeteorologicalForecast from "./ProfileDamMeteorologicalForecast";
    import ProfileDamMeteorologicalStation from "./ProfileDamMeteorologicalStation";
    import ProfileDamGraphicCardLink from "./ProfileDamGraphicCardLink";
    import CarouselGalleryWrapper from "../shared/CarouselGalleryWrapper";
    import ModalDatePicker from "./ModalDatePicker";


    export default {
        name: "ProfileAside",
        components: {
            ProfileDamMeteorologicalForecast,
            ProfileDamMeteorologicalStation,
            ProfileDamGraphicCardLink,
            CarouselGalleryWrapper,
            ModalDatePicker
        },
        async mounted() {
            await this.dispatchGetImg();
        },
        data() {
            return {
                selectedDate: null,
                date: new Date(),
                data: {
                    "folder": "",
                    "images": []
                },
                selectedDateImage: null,
                singleImage: false,
                weatherStatus: {},
                loading: false,
                error: false,
                forceKey: 0
            }
        },
        computed: {
            ...mapGetters([
                'currentDamSelectedId',
                'currentDamSelectedCoordinates',
                'currentDamSelectedName',
                GET_IS_MOBILE_GRAPHIC_SHOWN,
                IS_MOBILE
            ]),
            /**
             * Try to parse the image name to get the last update time.
             * Example of raw name: picture_2020-12-04_07:04:02.png
             */
            shortDateCard() {
                if (this.data["images"].length > 0) {
                    // get the raw name from last item
                    // (slice returns an array, but as it is of 1 element, get first)
                    const rawDateString = this.data["images"].slice(-1)[0];

                    // get the index of picture_ and .png
                    const underscoreIndex = rawDateString.indexOf('_');
                    const extensionIndex = rawDateString.indexOf('.');

                    // remove those
                    const onlyDateTimeStr = rawDateString.substring(underscoreIndex, extensionIndex);
                    // split the date and the time
                    return onlyDateTimeStr.split("_").filter((element) => element !== "" );
                }
                return [];
            },
            shortDateOnly() {
                return formatShortDate(this.date);
            },
            /**
             * Build urls for each image found in data. Item at index 0 is the folder!
             * If direct links are provided, use them directly, otherwise build path to file.
             * NOTE: in production no links should be passed, but it is still a good option.
             * @returns {string|string}
             */
            cameraImage() {
                let linkToImage = '';

                // shown image in the right panel is the last available, so slice at last point
                if (this.data["images"].length > 0) {
                    linkToImage = `/media/${this.data["folder"]}/${this.data["images"].slice(-1)[0]}`;
                }
                return linkToImage;
            },
            imagesList() {
                const customSelected = this.selectedDateImage;
                let imagesList = [];
                let folder;
                let imagesArr = [];

                if (customSelected) {
                    folder = this.selectedDateImage["folder"];
                    imagesArr = this.selectedDateImage["images"];
                } else if (this.data["images"].length > 0 && !customSelected) {
                    folder = this.data["folder"];
                    imagesArr = this.data["images"];
                }

                if (folder) {
                    for (let index = 0; index < imagesArr.length; index++) {
                        imagesList.push(`/media/${folder}/${imagesArr[index]}`);
                    }
                }

                if (imagesList && imagesList.length > 0) {
                    // insert the last item at index 0 as to be shown first
                    const lastImageItem = imagesList.pop();
                    imagesList.unshift(lastImageItem);
                }

                return imagesList;
            }
        },
        methods: {
            ...mapActions([AUTH_LOGOUT]),
            /**
             * Get camera images or take a new image. Taking a new image is
             * an action for users with sufficient rights only!
             * Params are a subset of the old params used.
             * @returns {Promise<void>}
             */
            async dispatchGetImg(date) {
                try {

                    const params = {
                        "select_day": date || '',  // empty date means last update available is requested
                        "ps_name_index": this.currentDamSelectedId
                    };

                    const {data} = await apiCall.getCameraImages(params);

                    if (date) {
                        // stores the images for selected day and keep the initial selected data in this.data
                        this.selectedDateImage = data.result;
                        this.selectedDate = date;
                    } else {
                        this.data = data.result;
                        // sets the calendar correct date
                        this.getLastDate(data.result.folder);
                    }
                    this.loading = false;
                } catch (error) {

                    this.loading = false;
                    this.error = true;
                    console.log(error);

                    if (error.request.status === 401) {
                        // if resp is unauthorized, logout
                        this.AUTH_LOGOUT();
                    }
                }
            },

            openModal() {
                return this.$refs['camera-modal'].show();
            },
            getLastDate(folder) {
                const date = folder.split('/');
                this.selectedDate = date.pop();
            }
        },
        watch: {
            imagesList() {
                // force update the gallery wrapper
                this.forceKey++;
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "src/assets/scss/_cardDetail";

    .dam-profile-card {
        &-image {
            cursor: pointer;
        }
    }

    @media (max-width: 768px) {
        .dam-profile-card-wrapper {
            .dam-profile-card {
                min-height: 233px;
            }
        }
    }
</style>